import { useTranslation } from 'react-i18next'

import { Box, Flex, Text } from '@enechain/ecloud-designsystem'

import { HelpIcon } from '~/components/common/HelpIcon'
import { useCompanyInfo } from '~/hooks/useCompanyInfo'

/**
 * 会社情報カード (`admin-hub.md` §2 / AC-AHB-003〜005)。
 *
 * 自社の 登録小売電気事業者 登録番号 (画面ラベルは「登録番号」) と 名称 を出す。
 * 値は JEPX 自由記入欄の判別コメント組み立て (spot-bidding.md §7) と同じ会社情報で、
 * **顧客側では表示のみ** (AC-AHB-005)。 変更は enechain 運営に依頼する運用なので、
 * 編集の導線 (入力欄・編集ボタン) は置かない。
 *
 * 未登録は「未設定 (enechain 運営にお問い合わせください)」 のプレースホルダ
 * (AC-AHB-004)。 取得失敗も `useCompanyInfo` が未登録扱いに倒すので同じ表示になる
 * (誤った値を出すより「未設定」 と読ませる方が安全。 hook 側にも同じ判断が書いてある)。
 *
 * 取得中はプレースホルダを出さない。 出すと登録済みの事業者に「未設定」 が一瞬見えて、
 * 運営への問い合わせを促してしまう。
 */
export const CompanyInfoCard = (): React.ReactElement => {
  const { t } = useTranslation()
  const { isLoading, registered, retailerId, name } = useCompanyInfo()

  /** 未登録 / 取得失敗の時に 登録番号・名称 の両方へ出す文。 */
  const placeholder = t('page:adminHub.companyInfo.unset')

  return (
    // design `admin/index.html` の `.company-card` (角丸 8px + 影 sm)。
    <Box
      padding={4}
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="lg"
      boxShadow="shadSm"
      backgroundColor="Surface.primary"
    >
      <Flex flexDirection="column" gap={2}>
        {/* design `.company-card-title` (lg / 700) + 表示専用の注記は (?) の tooltip に集約
            (design 2026-07-27 デザイナー判断: 注記文を常時出すと冗長なため)。 */}
        <Flex alignItems="center" gap={1}>
          <Text as="h2" textStyle="cardTitle" color="Text.highEmphasis">
            {t('page:adminHub.companyInfo.title')}
          </Text>
          <HelpIcon
            ariaLabel={t('page:adminHub.companyInfo.note')}
            content={t('page:adminHub.companyInfo.note')}
          />
        </Flex>
        {/* design `.company-card-body`: ラベル + 値の組を横に並べ、 狭い幅では折り返す
            (値は契約名義で長くなりうる)。 */}
        <Flex flexWrap="wrap" rowGap={2} columnGap={6}>
          <Field
            label={t('page:adminHub.companyInfo.registrationNumber')}
            value={isLoading ? '' : registered ? retailerId : placeholder}
          />
          <Field
            label={t('page:adminHub.companyInfo.name')}
            value={isLoading ? '' : registered ? name : placeholder}
          />
        </Flex>
      </Flex>
    </Box>
  )
}

/** ラベル (上) + 値 (下) の 1 組。 */
const Field = ({
  label,
  value,
}: {
  label: string
  value: string
}): React.ReactElement => (
  // design `.company-card-item`: ラベル = md / 400 / mid、 値 = lg / 600 / high (数字は等幅)。
  <Flex flexDirection="column" gap="2px" minWidth={0}>
    <Text textStyle="body" color="Text.midEmphasis">
      {label}
    </Text>
    {/* 取得中は空。 高さを保って値が入った時に行が動かないよう nbsp を置く。 */}
    <Text
      textStyle="infoCardValue"
      color="Text.highEmphasis"
      fontVariantNumeric="tabular-nums"
    >
      {value === '' ? ' ' : value}
    </Text>
  </Flex>
)
